import { Button, Loader, Select, Textarea } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { IconDownload, IconPlus, IconSend2 } from "@tabler/icons-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";

import { assistantApi } from "#/api/assistantApi";
import { ChatAvatar } from "#/components/assistant/ChatAvatar";
import { GlowIconButton } from "#/components/assistant/GlowIconButton";
import { TrexLogo } from "#/components/assistant/TrexLogo";
import { MarkdownMessage } from "#/components/assistant/MarkdownMessage";
import { ThinkingIndicator } from "#/components/assistant/ThinkingIndicator";
import {
  showErrorNotification,
  showSuccessNotification,
} from "#/components/notifications";
import { useAssistantChat } from "#/hook/useAssistantChat";
import { useAuth } from "#/hook/useAuth";
import { AssistantTone } from "#/types/assistant";
import { exportChatToPdf } from "#/utils/exportChatPdf";

/**
 * Full-page T-Rex assistant — a ChatGPT/Claude-style conversation surface
 * (empty-state hero + suggestions, centered message column, sticky composer).
 * Gated on the per-user `/assistant/config` enabled flag, so it self-redirects
 * when the assistant isn't enabled for the current user.
 */
export const AssistantPage = () => {
  const { messages, isSending, tone, setTone, sendMessage, clear } =
    useAssistantChat();
  const { user } = useAuth();
  const { t } = useTranslation();

  const [input, setInput] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef<HTMLDivElement>(null);

  const { data: config, isLoading } = useQuery({
    queryKey: ["assistant-config"],
    queryFn: async () => (await assistantApi.getConfig()).data,
    staleTime: 5 * 60 * 1000,
  });

  const userInitials = useMemo(() => {
    const source = user?.name?.trim() || user?.email?.trim() || "";
    if (!source) return "?";
    const parts = source.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return source.slice(0, 2).toUpperCase();
  }, [user?.name, user?.email]);

  const toneOptions = [
    { value: AssistantTone.AUTO, label: t("assistant.tone.conversational") },
    { value: AssistantTone.PLAIN_ENGLISH, label: t("assistant.tone.plainEnglish") },
    { value: AssistantTone.STRICT_DATA, label: t("assistant.tone.dataOnly") },
  ];

  const suggestions = [
    t("assistant.suggestions.s1"),
    t("assistant.suggestions.s2"),
    t("assistant.suggestions.s3"),
  ];

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, isSending]);

  const submit = () => {
    const text = input.trim();
    if (!text || isSending) return;
    sendMessage(text);
    setInput("");
  };

  const handleExport = async () => {
    if (!messagesRef.current || messages.length === 0 || isExporting) return;
    setIsExporting(true);
    try {
      await exportChatToPdf(messagesRef.current);
      showSuccessNotification({ message: "Chat exported as PDF." });
    } catch {
      showErrorNotification({ message: "Failed to export chat." });
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader color="var(--assistant-accent)" />
      </div>
    );
  }

  // Assistant disabled for this user (admin, disabled client/user, or the
  // service is unavailable) — nothing to show; send them back.
  if (!config?.enabled) {
    return <Navigate to="/dashboard" replace />;
  }

  const hasMessages = messages.length > 0;

  return (
    <div className="flex h-full flex-col font-[family-name:var(--font-app)]">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-2 border-b border-gray-100 pb-3">
        <div className="flex items-center gap-2">
          <TrexLogo className="h-8 w-8" />
          <span className="text-lg font-semibold text-slate-800">
            {t("nav.trexAi")}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Select
            size="xs"
            className="w-40"
            data={toneOptions}
            value={tone}
            onChange={(value) =>
              setTone((value as AssistantTone) ?? AssistantTone.AUTO)
            }
            allowDeselect={false}
            comboboxProps={{ withinPortal: true }}
            aria-label={t("assistant.tone.conversational")}
          />
          <Button
            size="xs"
            variant="light"
            color="gray"
            leftSection={<IconDownload size={16} />}
            onClick={() => void handleExport()}
            loading={isExporting}
            disabled={!hasMessages}
          >
            {t("assistant.export")}
          </Button>
          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlus size={16} />}
            onClick={() => clear()}
            disabled={!hasMessages || isSending}
          >
            {t("assistant.newChat")}
          </Button>
        </div>
      </div>

      {/* Messages / empty state */}
      <div className="flex-1 overflow-y-auto">
        <div ref={messagesRef} className="mx-auto w-full max-w-3xl px-4 py-6">
          {!hasMessages && !isSending ? (
            <div className="flex h-full min-h-[50vh] flex-col items-center justify-center gap-5 text-center">
              <TrexLogo className="h-20 w-20" />
              <div>
                <h1 className="text-2xl font-bold text-slate-800">
                  {t("assistant.pageTitle")}
                </h1>
                <p className="mt-1 text-sm text-slate-500">
                  {t("assistant.tagline")}
                </p>
              </div>
              <div className="flex w-full max-w-lg flex-col gap-2">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => sendMessage(s)}
                    className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-left text-sm text-slate-700 transition-colors hover:border-[var(--assistant-accent-soft)] hover:bg-[var(--assistant-accent)]/5"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              {messages.map((message) =>
                message.role === "user" ? (
                  <div key={message.id} className="flex items-start justify-end gap-3">
                    <div className="max-w-[80%] whitespace-pre-wrap rounded-2xl rounded-br-sm bg-gradient-to-br from-[var(--assistant-from)] to-[var(--assistant-to)] px-4 py-2.5 text-sm text-white shadow-[0_4px_14px_-4px_var(--assistant-glow)]">
                      {message.content}
                    </div>
                    <ChatAvatar variant="user" initials={userInitials} />
                  </div>
                ) : (
                  <div key={message.id} className="flex items-start justify-start gap-3">
                    <ChatAvatar variant="assistant" />
                    <div className="max-w-[80%] rounded-2xl rounded-bl-sm border border-gray-100 bg-white px-4 py-2.5 text-slate-800 shadow-sm">
                      <MarkdownMessage content={message.content} />
                    </div>
                  </div>
                )
              )}

              {isSending && (
                <div className="flex items-start justify-start gap-3">
                  <ChatAvatar variant="assistant" />
                  <div className="rounded-2xl rounded-bl-sm border border-gray-100 bg-white px-4 py-2.5 shadow-sm">
                    <ThinkingIndicator />
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>
          )}
        </div>
      </div>

      {/* Composer */}
      <div className="border-t border-gray-100 pt-3">
        <div className="mx-auto w-full max-w-3xl px-4">
          <div className="flex items-end gap-2 rounded-2xl border border-gray-200 bg-white px-3 py-2 shadow-sm focus-within:border-[var(--assistant-accent-soft)]">
            <Textarea
              className="flex-1"
              variant="unstyled"
              placeholder={t("assistant.placeholder")}
              autosize
              minRows={1}
              maxRows={6}
              value={input}
              onChange={(event) => setInput(event.currentTarget.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  submit();
                }
              }}
              disabled={isSending}
            />
            <GlowIconButton
              icon={IconSend2}
              variant="solid"
              onClick={submit}
              disabled={isSending || input.trim().length === 0}
              ariaLabel="Send"
            />
          </div>
          <div className="flex items-center justify-between px-1 py-1.5 text-2xs text-amber-700">
            <span>{t("assistant.disclaimer")}</span>
            {config.dailyLimit > 0 && (
              <span
                className={`shrink-0 pl-3 ${
                  config.remaining <= 0
                    ? "font-medium text-red-500"
                    : config.remaining <= 3
                      ? "text-amber-500"
                      : "text-slate-400"
                }`}
              >
                {t("assistant.messagesLeft", {
                  remaining: config.remaining,
                  limit: config.dailyLimit,
                })}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssistantPage;
